import type { LngLatBoundsLike, LngLatLike, MapOptions } from 'mapbox-gl';
import { BD_GEOGRAPHIC_GERMANY_BBOX } from '../geographic-values';

const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;

function isFiniteInRange(value: unknown, max: number): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value >= -max && value <= max;
}

function extractLngLat(
  value: LngLatLike,
): { lng: number; lat: number } | null {
  if (Array.isArray(value)) {
    return { lng: value[0], lat: value[1] };
  }
  if (value && typeof value === 'object') {
    const candidate = value as { lng?: number; lon?: number; lat?: number };
    const lng = candidate.lng ?? candidate.lon;
    const lat = candidate.lat;
    if (typeof lng === 'number' && typeof lat === 'number') {
      return { lng, lat };
    }
  }
  return null;
}

/**
 * Returns true only for a `LngLatLike` whose longitude and latitude are finite
 * and inside Mapbox' accepted range (lat -90..90, lng -180..180).
 *
 * Guards against the `Invalid LngLat latitude value` exception Mapbox throws in
 * the `Map` constructor when an empty data set produces a non-finite camera
 * coordinate (e.g. `@turf/bbox` returning `Infinity` for an empty
 * FeatureCollection).
 */
export function isValidLngLat(value: LngLatLike | undefined | null): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  const coords = extractLngLat(value);
  if (!coords) {
    return false;
  }
  return (
    isFiniteInRange(coords.lng, MAX_LONGITUDE) &&
    isFiniteInRange(coords.lat, MAX_LATITUDE)
  );
}

/**
 * Returns true only for a `LngLatBoundsLike` whose corners are all finite and
 * inside Mapbox' accepted range.
 */
export function isValidLngLatBounds(
  value: LngLatBoundsLike | undefined | null,
): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (Array.isArray(value)) {
    // [west, south, east, north]
    if (value.length === 4 && value.every((v) => typeof v === 'number')) {
      const [west, south, east, north] = value as [number, number, number, number];
      return (
        isFiniteInRange(west, MAX_LONGITUDE) &&
        isFiniteInRange(south, MAX_LATITUDE) &&
        isFiniteInRange(east, MAX_LONGITUDE) &&
        isFiniteInRange(north, MAX_LATITUDE)
      );
    }
    // [LngLatLike, LngLatLike]
    if (value.length === 2) {
      return (
        isValidLngLat(value[0] as LngLatLike) &&
        isValidLngLat(value[1] as LngLatLike)
      );
    }
    return false;
  }
  // LngLatBounds instance
  const bounds = value as {
    getSouthWest?: () => LngLatLike;
    getNorthEast?: () => LngLatLike;
  };
  if (
    typeof bounds.getSouthWest === 'function' &&
    typeof bounds.getNorthEast === 'function'
  ) {
    return (
      isValidLngLat(bounds.getSouthWest()) &&
      isValidLngLat(bounds.getNorthEast())
    );
  }
  return false;
}

/**
 * Merges a stored camera (a saved map extent) onto a set of default map
 * options.
 *
 * When the stored camera positions the map itself — it carries a valid
 * `center` — the defaults' `bounds` is dropped. Mapbox' `Map` constructor
 * applies `fitBounds(bounds)` *after* `jumpTo({ center, zoom })`, so a `bounds`
 * left in place silently overrides the stored extent every time the map is
 * created. The extent then only ever showed while the map happened to survive,
 * and any rebuild (saving the analysis, paging, reopening it) snapped the view
 * back to the default bounding box (BDA-6238).
 *
 * Without a stored position the `bounds` is kept: it is what gives such a map
 * its initial camera.
 */
export function mergeStoredCamera(
  defaults: MapOptions,
  stored: Partial<MapOptions> | null | undefined,
): MapOptions {
  const merged: MapOptions = { ...defaults, ...(stored ?? {}) };

  if (isValidLngLat(merged.center)) {
    delete merged.bounds;
  }

  return merged;
}

/**
 * Returns a copy of the given map options with the camera (`center` / `bounds`)
 * guarded against non-finite / out-of-range coordinates.
 *
 * - An invalid `center` or `bounds` is dropped so it never reaches the Mapbox
 *   `Map` constructor (which would throw `Invalid LngLat ...`).
 * - If neither a valid `center` nor valid `bounds` remains, falls back to the
 *   default Germany bounding box so the map still initialises.
 */
export function sanitizeMapCameraOptions(options: MapOptions): MapOptions {
  const sanitized: MapOptions = { ...options };

  const hasValidCenter = isValidLngLat(sanitized.center);
  const hasValidBounds = isValidLngLatBounds(sanitized.bounds);

  if (!hasValidCenter && sanitized.center !== undefined) {
    delete sanitized.center;
  }
  if (!hasValidBounds && sanitized.bounds !== undefined) {
    delete sanitized.bounds;
  }

  if (!hasValidCenter && !hasValidBounds) {
    sanitized.bounds = BD_GEOGRAPHIC_GERMANY_BBOX;
  }

  return sanitized;
}

/**
 * The part of a rendered map this module needs to read a live camera from.
 *
 * Typed structurally on purpose: `libs/domain` may only `import type` from
 * `mapbox-gl`, so the real `Map` class must not be referenced here.
 */
export interface BdMapCameraSource {
  getCenter(): { lng: number; lat: number };
  getZoom(): number;
}

/**
 * What the map-settings dialog should treat as "the current view": the map
 * options in effect, with the live camera of the rendered map layered on top.
 *
 * Without the live camera the dialog can only ever offer a zoom — the options
 * carry no `center` until one is stored — and a zoom alone is not restorable:
 * {@link mergeStoredCamera} keeps the defaults' `bounds` for a camera without a
 * center, and a result map that follows its layer bounds refits over the extent
 * on every re-application. The dialog's zoom field is then inert (BDA-6250).
 *
 * Passing the live camera means switching "Kartenausschnitt festlegen" on
 * always yields a complete extent, which both mechanisms honour.
 */
export function currentCameraOptions(
  options: MapOptions,
  map: BdMapCameraSource | null | undefined,
): MapOptions {
  const current: MapOptions = JSON.parse(JSON.stringify(options));

  if (map) {
    const center = map.getCenter();
    current.center = { lng: center.lng, lat: center.lat };
    current.zoom = map.getZoom();
  }

  return current;
}
